import { AgGridColumn } from 'ag-grid-react';
import { Dictionary } from 'lodash';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Team, User } from 'api/models';
import { ResourceType } from 'api/resource-management/types';
import { Button } from 'components/atoms/button';
import { CustomLayoutDrawerContent as DrawerContent } from 'components/atoms/drawer';
import { Grid } from 'components/atoms/grid';
import { TableSkeleton } from 'components/atoms/table';
import {
  AllocationDetail,
  AllocationDetailLabel,
  AllocationDetailRow,
  AllocationDetailSecondaryValue,
  AllocationDetailValueWrapper,
  Drawer,
  GridContainer as CommonGridContainer,
  LoadingOverlay,
  UserOrTeamAvatarRow,
} from 'components/organisms/resource-management/auto-allocation-source-tasks';
import {
  TeamDetail,
  UserDetail,
} from 'components/organisms/resource-management/manage-allocation';
import { ignoreMinutesIfZero } from 'components/organisms/resource-management/utils/dates';
import { useGrid } from 'components/organisms/timesheets/drawers/calendar-integration/add-activity-page/activity-table';
import {
  ProjectTemplateTask,
  TemplateAllocation,
} from 'data/templates/projects';
import { useTranslate } from 'hooks/use-translation';
import {
  AssigneeRenderer,
  DurationRenderer,
  EffortRenderer,
  StartDayRenderer,
  TaskNameRenderer,
} from './table-cells';

const GridContainer = styled(CommonGridContainer)`
  .ag-row {
    cursor: default;
  }
`;

interface Props {
  userLookup: Dictionary<User>;
  teamLookup: Dictionary<Team>;
  allocation?: TemplateAllocation;
  sourceTasksGetter: (
    allocation: TemplateAllocation,
  ) => Promise<ProjectTemplateTask[]>;
  onCancel: () => void;
  isHoursPerWeek: boolean;
}

export const AutoAllocateSourceTasksDrawer: FC<Props> = ({
  allocation,
  userLookup,
  teamLookup,
  sourceTasksGetter,
  onCancel,
  isHoursPerWeek,
}) => {
  const translate = useTranslate();
  const user = useMemo(
    () =>
      allocation != null && allocation?.type === ResourceType.User
        ? userLookup[allocation.userId]
        : undefined,
    [allocation, userLookup],
  );
  const team = useMemo(
    () =>
      allocation != null && allocation?.type === ResourceType.Team
        ? teamLookup[allocation.teamId]
        : undefined,
    [allocation, teamLookup],
  );

  const [isSourceTasksFetching, setSourceTasksFetchingStatus] = useState(false);
  const { gridApi, onGridReady } = useGrid();

  useEffect(() => {
    if (allocation != null && gridApi != null) {
      setSourceTasksFetchingStatus(true);
      sourceTasksGetter(allocation)
        .then((tasks) => {
          gridApi.setRowData(tasks);
        })
        .finally(() => {
          setSourceTasksFetchingStatus(false);
        });
    }
  }, [allocation, gridApi, sourceTasksGetter]);

  return (
    <Drawer visible={allocation != null} onClose={onCancel} destroyOnClose>
      <DrawerContent>
        <DrawerContent.Title>
          <FormattedMessage id="resource_management_auto_allocation.source_tasks.title" />
        </DrawerContent.Title>
        <DrawerContent.Content>
          <UserOrTeamAvatarRow>
            {user != null ? (
              <UserDetail user={user} />
            ) : team != null ? (
              <TeamDetail team={team} />
            ) : null}
          </UserOrTeamAvatarRow>
          {allocation != null ? (
            <AllocationDetailRow>
              <AllocationDetail>
                <AllocationDetailLabel>
                  {isHoursPerWeek ? (
                    <FormattedMessage id="resource_management.allocations.number_of_weeks.label" />
                  ) : (
                    <FormattedMessage
                      id="resource_management.allocations.number_of_working_days.label"
                      values={{ showDates: false }}
                    />
                  )}
                </AllocationDetailLabel>
                <AllocationDetailValueWrapper>
                  {allocation.duration / (isHoursPerWeek ? 5 : 1)}
                  <AllocationDetailSecondaryValue>
                    (
                    <FormattedMessage
                      id="templates.resource_management.tooltips.dates"
                      values={{
                        startDay: allocation.startDay,
                        duration: allocation.duration,
                      }}
                    />
                    )
                  </AllocationDetailSecondaryValue>
                </AllocationDetailValueWrapper>
              </AllocationDetail>
              <AllocationDetail>
                <AllocationDetailLabel>
                  <FormattedMessage
                    id={
                      isHoursPerWeek
                        ? 'resource_management.allocations.time_per_week.label'
                        : 'resource_management.auto_allocate.time_per_day.label'
                    }
                  />
                </AllocationDetailLabel>
                <AllocationDetailValueWrapper>
                  {ignoreMinutesIfZero(
                    allocation.minutes * (isHoursPerWeek ? 5 : 1),
                  )}
                </AllocationDetailValueWrapper>
              </AllocationDetail>
              <AllocationDetail>
                <AllocationDetailLabel>
                  <FormattedMessage id="resource_management.allocations.total_allocation_only.label" />
                </AllocationDetailLabel>
                <AllocationDetailValueWrapper>
                  {ignoreMinutesIfZero(
                    allocation.minutes * allocation.duration,
                  )}
                </AllocationDetailValueWrapper>
              </AllocationDetail>
            </AllocationDetailRow>
          ) : null}
          <GridContainer className="ag-theme-alpine">
            <Grid
              suppressContextMenu
              suppressRowClickSelection
              suppressCellSelection
              suppressLoadingOverlay
              onGridReady={onGridReady}
              frameworkComponents={{
                EffortRenderer,
                AssigneeRenderer,
                DurationRenderer,
                StartDayRenderer,
                TaskNameRenderer,
              }}
              defaultColDef={{
                filter: false,
                sortable: false,
                resizable: false,
                suppressMovable: true,
                suppressMenu: true,
              }}
            >
              <AgGridColumn
                width={224}
                field="taskId"
                cellRenderer="TaskNameRenderer"
                editable={false}
                headerName={translate('task_name')}
              />
              <AgGridColumn
                width={120}
                field="effort"
                cellRenderer="EffortRenderer"
                editable={false}
                headerName={translate('effort')}
              />
              <AgGridColumn
                width={120}
                field="assignee"
                editable={false}
                cellRenderer="AssigneeRenderer"
                headerName={translate('assigned_to')}
              />
              <AgGridColumn
                width={92}
                field="startDay"
                editable={false}
                cellRenderer="StartDayRenderer"
                headerName={translate('templates.starts_on')}
              />
              <AgGridColumn
                width={92}
                field="duration"
                editable={false}
                cellRenderer="DurationRenderer"
                headerName={translate('templates.duration')}
              />
            </Grid>
            {isSourceTasksFetching ? (
              <LoadingOverlay>
                <TableSkeleton rowCount={6} columnCount={4} />
              </LoadingOverlay>
            ) : null}
          </GridContainer>
        </DrawerContent.Content>
        <DrawerContent.Footer>
          <Button type="primary" onClick={onCancel}>
            <FormattedMessage id="done" />
          </Button>
        </DrawerContent.Footer>
      </DrawerContent>
    </Drawer>
  );
};
